"use client";

import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Search, Users } from "lucide-react";
import { successToast,errorToast } from '../utils/toasters'
import GroupCard from "../components/groups/GroupCard";
import { UserCheck } from "../components/icons/Icons";
import MainLayout from "../components/MainLayout";
import { jwtDecode } from "jwt-decode";

import {
  handleJoinGroup,
  handleLeaveGroup,
  handlegetAllGroup,
} from "../services/group";
import { decode } from "punycode";

function GroupsPage() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [processingIds, setProcessingIds] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    fetchGroups();
  }, []);

  const fetchGroups = async () => {
    try {
      setLoading(true);
      const geoHash = localStorage.getItem("geohash") || "9v6m";
      const token = localStorage.getItem("access_token");
      let userId = null;
      if (token) {
        const decoded = jwtDecode(token);
        userId = decoded?.userid || decoded?.id || null;
      }
      const response = await handlegetAllGroup({ geoHash, userId });
      if (response && response.data) {
        setGroups(response.data.groups);
      }
    } catch (error) {
      console.error("Error fetching groups:", error);
      errorToast("Failed to fetch groups");
    } finally {
      setLoading(false);
    }
  };

  const handleJoin = async (groupId) => {
    if (processingIds.includes(groupId)) return;

    // Check if user is logged in
    const access_token = localStorage.getItem("access_token");
    if (!access_token) {
      successToast("Please login to join groups", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
      });
      navigate("/signin", { state: { from: `/groups/${groupId}` } });
      return;
    }

    try {
      setProcessingIds((prev) => [...prev, groupId]);
      const response = await handleJoinGroup(groupId);

      setGroups((prevGroups) =>
        prevGroups.map((group) =>
          group.groupid === groupId
            ? {
                ...group,
                isJoined: true,
                member_count: Number.parseInt(group.member_count || 0, 10) + 1,
              }
            : group
        )
      );

      successToast("Successfully joined the group!", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
      });
    } catch (error) {
      errorToast(
        "" +
          (error.response?.data?.message ??
            error.data?.message ??
            error.message ??
            "Failed to join group"),
        {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
        }
      );
    } finally {
      setProcessingIds((prev) => prev.filter((id) => id !== groupId));
    }
  };

  const handleLeave = async (groupId) => {
    if (processingIds.includes(groupId)) return;

    // Check if user is logged in
    const access_token = localStorage.getItem("access_token");
    if (!access_token) {
      successToast("Please login to manage group membership", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
      });
      // Redirect to login page
      navigate("/login", { state: { from: `/groups/${groupId}` } });
      return;
    }

    try {
      setProcessingIds((prev) => [...prev, groupId]);
      const response = await handleLeaveGroup(groupId);

      // Update the specific group in state
      setGroups((prevGroups) =>
        prevGroups.map((group) =>
          group.groupid === groupId
            ? {
                ...group,
                isJoined: false,
                member_count: Math.max(
                  0,
                  Number.parseInt(group.member_count || 0, 10) - 1
                ),
              }
            : group
        )
      );

      successToast("Successfully left the group", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
      });
    } catch (error) {
      errorToast(
        "" +
          (error.response?.data?.message ??
            error.data?.message ??
            error.message ??
            "Failed to leave group"),
        {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
        }
      );
    } finally {
      setProcessingIds((prev) => prev.filter((id) => id !== groupId));
    }
  };

  const filteredGroups = groups.filter((group) => {
    // Check if the text matches search query
    const matchesSearch =
      group.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      group.description?.toLowerCase().includes(searchQuery.toLowerCase());

    // Apply filter based on joined status
    if (filter === "joined") {
      return matchesSearch && group.isJoined;
    }

    return matchesSearch;
  });

  return (
    <MainLayout rs={false}>
      <div className="container mx-auto px-4 py-6 max-w-6xl">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold font-fraunces">Groups</h1>
          <Link
            to="/groups/create"
            className="bg-[#7B189F] text-white px-4 py-2 rounded-lg flex items-center hover:bg-purple-700 transition-colors font-afacad"
          >
            <Plus className="h-5 w-5 mr-1" />
            Create Group
          </Link>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-grow">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <input
                type="text"
                placeholder="Search groups..."
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 font-afacad"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div className="flex gap-2">
              <button
                className={`px-4 py-2 rounded-lg flex items-center font-afacad ${
                  filter === "all"
                    ? "bg-purple-100 text-purple-700"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
                onClick={() => setFilter("all")}
              >
                <Users className="h-4 w-4 mr-1" />
                All
              </button>
              <button
                className={`px-4 py-2 rounded-lg flex items-center font-afacad ${
                  filter === "joined"
                    ? "bg-purple-100 text-purple-700"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
                onClick={() => setFilter("joined")}
              >
                <UserCheck className="h-4 w-4 mr-1" />
                Joined
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div
                key={i}
                className="h-[280px] bg-gray-100 rounded-lg animate-pulse"
              ></div>
            ))}
          </div>
        ) : filteredGroups.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredGroups.map((group) => (
              <GroupCard
                key={group.groupid || group.id}
                group={group}
                onJoin={handleJoin}
                onLeave={handleLeave}
                isProcessing={processingIds.includes(group.groupid)}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="bg-gray-100 rounded-full h-16 w-16 flex items-center justify-center mx-auto mb-4">
              <Users className="h-8 w-8 text-gray-500" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-1 font-fraunces">
              No groups found
            </h3>
            <p className="text-gray-500 mb-4 font-afacad">
              Try adjusting your search or filters
            </p>
            <Link
              to="/groups/create"
              className="inline-flex items-center px-4 py-2 bg-[#7B189F] text-white rounded-lg hover:bg-purple-700 transition-colors font-afacad"
            >
              <Plus className="h-5 w-5 mr-1" />
              Create a new group
            </Link>
          </div>
        )}
      </div>
    </MainLayout>
  );
}

export default GroupsPage;
